import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Trash2, Wrench, CalendarCheck, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const COLORS = { booking: "#006233", reserved: "#0B132B", maintenance: "#D97706" };
const LABELS = { booking: "Location acceptée", reserved: "Réservé (bloqué)", maintenance: "Maintenance" };
const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

const pad = (n) => String(n).padStart(2, "0");
const monthKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;

export const PlanningTab = ({ vehicles }) => {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ vehicle_id: "", start_date: "", end_date: "", reason: "reserved", note: "" });

  const key = monthKey(cursor);
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => `${key}-${pad(i + 1)}`);
  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/owner/calendar", { params: { month: key } }).then((r) => setEvents(r.data)).finally(() => setLoading(false));
  }, [key]);
  useEffect(() => { load(); }, [load]);

  const eventAt = (vehicleId, day) => events.find((e) => e.vehicle_id === vehicleId && e.start_date <= day && e.end_date >= day);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/owner/blocks", form);
      toast.success("Dates bloquées");
      setOpen(false);
      setForm({ vehicle_id: "", start_date: "", end_date: "", reason: "reserved", note: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/owner/blocks/${id}`);
    toast.success("Blocage supprimé");
    load();
  };

  const blocks = events.filter((e) => e.deletable).sort((a, b) => a.start_date.localeCompare(b.start_date));

  return (
    <div className="space-y-6" data-testid="planning-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="planning-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
          <h3 className="font-display text-xl font-extrabold text-slate-900 capitalize min-w-[180px] text-center" data-testid="planning-month-label">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</h3>
          <Button variant="outline" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="planning-next-month"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-600">
          {Object.keys(COLORS).map((k) => <span key={k} className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm" style={{ background: COLORS[k] }} /> {LABELS[k]}</span>)}
          <Button onClick={() => setOpen(true)} className="text-white" style={{ background: "#006233" }} data-testid="planning-add-block-btn"><Plus className="h-4 w-4 mr-1.5" /> Bloquer des dates</Button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
          <table className="text-xs border-collapse min-w-full" data-testid="planning-grid">
            <thead>
              <tr>
                <th className="sticky left-0 bg-white px-3 py-2 text-left font-semibold text-slate-600 border-b border-r border-slate-200 min-w-[160px]">Véhicule</th>
                {days.map((d) => <th key={d} className={`px-0 py-2 border-b border-slate-200 font-medium w-7 ${d === today ? "text-red-600" : "text-slate-400"}`}>{d.slice(8)}</th>)}
              </tr>
            </thead>
            <tbody>
              {vehicles.map((v) => (
                <tr key={v.id} data-testid={`planning-row-${v.id}`}>
                  <td className="sticky left-0 bg-white px-3 py-2 font-semibold text-slate-800 border-b border-r border-slate-100 whitespace-nowrap">{v.brand} {v.model}</td>
                  {days.map((d) => {
                    const ev = eventAt(v.id, d);
                    return (
                      <td key={d} className="border-b border-slate-100 p-0.5" title={ev ? `${LABELS[ev.type]} · ${ev.label}` : ""}>
                        <div className="h-7 w-full rounded-sm" style={{ background: ev ? COLORS[ev.type] : "#f8fafc" }} data-testid={ev ? `planning-cell-${v.id}-${d}` : undefined} />
                      </td>
                    );
                  })}
                </tr>
              ))}
              {vehicles.length === 0 && <tr><td colSpan={days.length + 1} className="px-4 py-10 text-center text-slate-500">Ajoutez un véhicule pour voir le planning.</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h4 className="font-semibold text-slate-900 mb-3">Blocages ce mois-ci</h4>
        {blocks.length === 0 ? <p className="text-sm text-slate-500">Aucun blocage manuel sur ce mois.</p> : (
          <ul className="divide-y divide-slate-100">
            {blocks.map((b) => (
              <li key={b.id} className="flex items-center justify-between py-2.5 text-sm" data-testid={`planning-block-${b.id}`}>
                <div className="flex items-center gap-3">
                  {b.type === "maintenance" ? <Wrench className="h-4 w-4" style={{ color: COLORS.maintenance }} /> : <CalendarCheck className="h-4 w-4" style={{ color: COLORS.reserved }} />}
                  <div>
                    <p className="font-medium text-slate-800">{vehicles.find((v) => v.id === b.vehicle_id)?.brand} {vehicles.find((v) => v.id === b.vehicle_id)?.model} · {LABELS[b.type]}</p>
                    <p className="text-xs text-slate-500">{b.start_date} → {b.end_date}{b.label && b.label !== "Réservé" && b.label !== "Maintenance" ? ` · ${b.label}` : ""}</p>
                  </div>
                </div>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(b.id)} data-testid={`planning-delete-block-${b.id}`}><Trash2 className="h-4 w-4" /></Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bloquer des dates</DialogTitle>
            <DialogDescription>Le véhicule n'apparaîtra pas dans les recherches sur cette période.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
              <SelectTrigger data-testid="block-vehicle-select" className="h-10"><SelectValue placeholder="Choisir un véhicule" /></SelectTrigger>
              <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.brand} {v.model}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <input required type="date" data-testid="block-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="h-10 w-full rounded-md border border-input px-3 text-sm" />
              <input required type="date" data-testid="block-end" min={form.start_date || undefined} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="h-10 w-full rounded-md border border-input px-3 text-sm" />
            </div>
            <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
              <SelectTrigger data-testid="block-reason-select" className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="reserved">Réservé (hors plateforme)</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
              </SelectContent>
            </Select>
            <input placeholder="Note (optionnel)" data-testid="block-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="h-10 w-full rounded-md border border-input px-3 text-sm" />
            <DialogFooter>
              <Button type="submit" disabled={saving || !form.vehicle_id} className="text-white" style={{ background: "#006233" }} data-testid="block-submit-btn">{saving ? "Enregistrement..." : "Bloquer"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
